import { useTranslation } from "react-i18next";
import type { HumanStepRequestedPayload, ThreadEntry } from "../../../../api/threads/liveMessages";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import AcceptResultStep from "../../../knowledgeData/results/AcceptResultStep";
import KeepMemoryStep from "../../../memories/steps/KeepMemoryStep";
import ReviewMemoryStep from "../../../memories/steps/ReviewMemoryStep";
import ReviewWorkspace from "../../../reviews/page/ReviewWorkspace";
import { HumanStepAnswer, HumanStepInputs } from "../../entries/HumanStepEntries";
import { responseDefinition, reviewedChangeSetOf, reviewPartOf } from "../../entries/humanStepResponse";
import { requestWaitText } from "../../status/requestWaitWords";
import TalkWithAgent from "../../talk/TalkWithAgent";
import { refusalText } from "../../../../ui/feedback/refusalWords";
import { useThreadContext } from "../threadPageContext";
import { actionDecision } from "../viewerActions";
import EditStepView from "./EditStepView";

interface WaitingStepViewProps {
  readonly entry: ThreadEntry;
  readonly payload: HumanStepRequestedPayload;
}

/**
 * The view of the person step that waits, in the current step's place (F33, ADR-0031): a
 * review step is the change set's review, whose decision is the step's answer (D4); an edit
 * step is the item's form, whose save is its answer (D2); a memory's keep and review steps
 * are the keep form and the memory with Confirm or Discard (D5); an acceptance step is the
 * result with Accept or Reject (ADR-0026 slice 3a); every other step is its
 * answer form. Curators who are not the host answer a review too, also while the Thread is saved
 * (P3), so the review checks its own permissions.
 */
export default function WaitingStepView({ entry, payload }: WaitingStepViewProps) {
  const { t } = useTranslation();
  const { threadId, overview, nameOf, viewerIsHost } = useThreadContext();
  const definition = responseDefinition(payload.response);
  const answering = actionDecision(overview.viewer, "answer_request", String(entry.number));
  const waiting = overview.projection.open_human_step;
  if (!answering.allowed) {
    // Module C's decision comes first, also before an owner's own view (a review, a result, a memory) adds its rules: a
    // closed Run takes no answer. Its log entry leaves the inputs to this view (F104), so they show here, to read.
    return (
      <>
        <HumanStepInputs inputs={payload.inputs} />
        {waiting == null ? null : <p className={FORM_HELP_TEXT_CLASS}>{requestWaitText(waiting, answering.refusal, viewerIsHost ? null : nameOf(overview.host), t)}</p>}
      </>
    );
  }
  // E2: every review offers Talk with the agent below it, as its common part says (proposal 0.8, O1); an input review shows
  // it in its own answer form. A talk runs the agent as a reply does (writer, own route, no open turn), so module C's reply
  // decision says who may.
  const talking = actionDecision(overview.viewer, "request_reply");
  const review = definition.kind === "review_output" ? null : reviewPartOf(definition);
  const talk = review?.talk === true ? (
    <TalkWithAgent
      threadId={threadId}
      reviewEntry={entry.number}
      reviewed={payload.inputs.find((input) => input.slot === review.input)?.value?.output_kind ?? null}
      result={null}
      disabled={false}
      unavailable={talking.refusal === null ? undefined : refusalText(t, talking.refusal)}
      onSent={() => undefined}
    />
  ) : null;
  if (definition.kind === "review_change_set") {
    const changeSetId = reviewedChangeSetOf(payload);
    return changeSetId === null ? (
      <p className={FORM_HELP_TEXT_CLASS}>{t("reviews.inThread.noChangeSet")}</p>
    ) : (
      <>
        <ReviewWorkspace key={changeSetId} changeSetId={changeSetId} place={{ kind: "step", threadId }} />
        {talk}
      </>
    );
  }
  if (definition.kind === "decide_results") {
    // A result's curators answer, not the Thread's writers (ADR-0026 slice 3a); the server decides who may.
    return (
      <>
        <AcceptResultStep threadId={threadId} entry={entry} payload={payload} />
        {talk}
      </>
    );
  }
  if (definition.kind === "review_memory") {
    // Whoever may approve answers, as curators do a change set's review; the memory says who may.
    return (
      <>
        <ReviewMemoryStep threadId={threadId} projectId={overview.projectId} entry={entry} payload={payload} />
        {talk}
      </>
    );
  }
  if (definition.kind === "propose_change") return <EditStepView entry={entry} payload={payload} />;
  if (definition.kind === "keep_memory") return <KeepMemoryStep threadId={threadId} projectId={overview.projectId} entry={entry} payload={payload} />;
  return <HumanStepAnswer entry={entry} payload={payload} />;
}
